/**
 * Phase 1 · CollectionAlbumSnapshotCard  ·  extended in Phase C1.
 *
 * One reusable presentation card built on top of the shared
 * `buildAlbumPresentation()` model. Two visual layouts:
 *
 *   layout="default"   (Phase 1 original)
 *     - Optional large 3:4 cover on top, all metadata stacked below.
 *
 *   layout="horizontal"  (Phase C1 unified card)
 *     - Small thumbnail (≈80–96px) on the left.
 *     - Official title (large/bold), variant label, optional nickname
 *       to the right, status pill top-right.
 *     - Progress bar + counts / collection-only message below the
 *       identity row (still inside the same card).
 *     - Bottom footer holds Open / Manage / footer slot.
 *
 * Both layouts derive every rendered value from
 * `buildAlbumPresentation(album)` — surfaces MUST NOT read the
 * backend's inverted `title` / `subtitle` fields directly.
 *
 * Two rendering modes remain the same regardless of layout:
 *
 *   TRACKING MODE (default)
 *     - Official title + cover label + optional nickname
 *     - Status pill ("In Progress" or "Completed")
 *     - Progress bar with percent
 *     - Owned / Missing / Duplicate counts (unless `showCounts=false`)
 *     - "100% COMPLETE" gold badge overlay when completed
 *
 *   COLLECTION-ONLY MODE (collection_method === "album_only")
 *     - Official title + cover label + optional nickname
 *     - Grey "Collection Only" pill
 *     - No percentage, no counts, no progress bar
 *     - Exact copy: "Stored empty without stickers applied."
 *
 * Never renders raw slugs, "Sealed", or "Ownership tracked" language.
 * Test IDs use the approved stable names:
 *   album-snapshot-card, album-official-title, album-cover-label,
 *   album-nickname, album-status, album-progress, album-progress-percent,
 *   album-owned-count, album-missing-count, album-duplicate-count,
 *   album-collection-only-message, album-completed-badge,
 *   album-thumbnail, album-thumbnail-placeholder.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Trophy, Package } from "lucide-react";
import {
  buildAlbumPresentation,
  COLLECTION_ONLY_MESSAGE,
} from "@/lib/albumPresentation";
import { AlbumNicknameEditor } from "@/components/collector/AlbumCardBits";

const STATUS_PILL = {
  collection_only: "bg-neutral-100 text-neutral-700 border-neutral-400",
  in_progress: "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)]",
  completed: "bg-emerald-100 text-emerald-900 border-emerald-900",
};

function StatusPill({ status, label }) {
  const cls = STATUS_PILL[status] || STATUS_PILL.in_progress;
  return (
    <span
      data-testid="album-status"
      data-status={status}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border-2 font-bold text-[10px] uppercase tracking-widest whitespace-nowrap ${cls}`}
    >
      {label}
    </span>
  );
}

function ProgressBar({ percent, isCompleted }) {
  const pct = Math.max(0, Math.min(100, percent ?? 0));
  const fill = isCompleted ? "bg-emerald-500" : "bg-[var(--panini-red)]";
  return (
    <div className="w-full" data-testid="album-progress">
      <div
        className="h-2 rounded-full overflow-hidden bg-[var(--stone)]"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full ${fill} transition-[width]`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] flex items-center justify-between">
        <span>Progress</span>
        <span
          className="text-[var(--ink)]"
          data-testid="album-progress-percent"
        >
          {pct}%
        </span>
      </div>
    </div>
  );
}

function Counts({ owned, missing, duplicate, showCollected, showMissing, showDuplicates }) {
  const cells = [
    showCollected && (
      <div key="collected" className="rounded-lg border border-[var(--stone)] bg-white/60 px-2 py-1.5">
        <div
          className="font-display font-bold text-base text-[var(--ink)]"
          data-testid="album-owned-count"
        >
          {owned}
        </div>
        <div className="text-[9px] uppercase tracking-widest font-bold text-[var(--smoke)]">
          Collected
        </div>
      </div>
    ),
    showMissing && (
      <div key="missing" className="rounded-lg border border-[var(--stone)] bg-white/60 px-2 py-1.5">
        <div
          className="font-display font-bold text-base text-[var(--panini-red)]"
          data-testid="album-missing-count"
        >
          {missing}
        </div>
        <div className="text-[9px] uppercase tracking-widest font-bold text-[var(--smoke)]">
          Missing
        </div>
      </div>
    ),
    showDuplicates && (
      <div key="duplicates" className="rounded-lg border border-[var(--stone)] bg-white/60 px-2 py-1.5">
        <div
          className="font-display font-bold text-base text-[var(--ink)]"
          data-testid="album-duplicate-count"
        >
          {duplicate}
        </div>
        <div className="text-[9px] uppercase tracking-widest font-bold text-[var(--smoke)]">
          Duplicates
        </div>
      </div>
    ),
  ].filter(Boolean);
  if (cells.length === 0) return null;
  const gridCols = cells.length === 3 ? "grid-cols-3"
                 : cells.length === 2 ? "grid-cols-2"
                                      : "grid-cols-1";
  return (
    <div className={`mt-3 grid ${gridCols} gap-2 text-center`}>
      {cells}
    </div>
  );
}

/**
 * Small horizontal thumbnail used by the horizontal card layout. Never
 * fabricates an image URL — accepts only `cover_image_url` from the
 * backend catalog and falls through to the safe placeholder icon.
 */
function Thumbnail({ url, variant }) {
  const box =
    "shrink-0 w-16 sm:w-24 aspect-[3/4] rounded-lg overflow-hidden bg-neutral-50 grid place-items-center " +
    (variant === "trophy"
      ? "border border-[#c8a24d]/40"
      : "border border-[var(--stone)]");
  if (url) {
    return (
      <div className={box} data-testid="album-thumbnail">
        <img
          src={url}
          alt=""
          className="max-w-full max-h-full w-auto h-auto object-contain object-center"
        />
      </div>
    );
  }
  return (
    <div className={box} data-testid="album-thumbnail-placeholder" aria-hidden>
      <Package size={26} className="text-[var(--smoke)]" />
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.album          Raw dashboard-summary or my-collection album row.
 * @param {"default"|"horizontal"} [props.layout="default"]
 *   "default"    → original vertical stacked layout (Phase 1).
 *   "horizontal" → compact horizontal layout with a small thumbnail (Phase C1).
 * @param {"tracking"|"trophy"} [props.variant="tracking"]
 *   "tracking" → mission card. "trophy" → gold-trimmed Hall of Fame card.
 * @param {(next:string)=>Promise} [props.onNicknameSave]  When present, nickname is editable.
 * @param {boolean} [props.showCover=true]   Render the cover art when the album row has one (default layout only).
 * @param {boolean} [props.showCounts=true]  Legacy switch — controls all three count cells at once.
 * @param {boolean} [props.showCollected]     Override for the Collected cell only (defaults to `showCounts`).
 * @param {boolean} [props.showMissing]       Override for the Missing cell only (defaults to `showCounts`).
 * @param {boolean} [props.showDuplicates]    Override for the Duplicates cell only (defaults to `showCounts`).
 * @param {React.ReactNode} [props.footer]   Optional footer slot (e.g. View Album / Certificate links).
 * @param {(album:object)=>void} [props.onOpen]  When present, an "Open Album" button appears in the footer.
 * @param {React.ReactNode} [props.manageSlot]   Optional trailing slot next to Open Album (e.g. Manage menu).
 */
export default function CollectionAlbumSnapshotCard({
  album,
  layout = "default",
  variant = "tracking",
  onNicknameSave = null,
  showCover = true,
  showCounts = true,
  showCollected,
  showMissing,
  showDuplicates,
  footer = null,
  onOpen = null,
  manageSlot = null,
}) {
  const p = buildAlbumPresentation(album);
  const trophy = variant === "trophy";
  const horizontal = layout === "horizontal";
  // Per-column visibility falls back to the legacy `showCounts`
  // switch when no explicit override is provided, so every existing
  // consumer keeps its current behavior.
  const sCollected  = showCollected  === undefined ? showCounts : showCollected;
  const sMissing    = showMissing    === undefined ? showCounts : showMissing;
  const sDuplicates = showDuplicates === undefined ? showCounts : showDuplicates;

  const rootClass = trophy
    ? "relative rounded-2xl border-2 border-[#c8a24d]/60 bg-gradient-to-br from-white to-[#fffaf0] p-4 shadow-[0_10px_30px_-15px_rgba(200,162,77,0.55)] overflow-hidden"
    : "relative rounded-2xl border-2 border-[var(--ink)] bg-white shadow-[3px_3px_0_var(--ink)] p-4 md:p-5 overflow-hidden";

  const identity = (
    <div className="min-w-0 flex-1 overflow-hidden">
      {p.openAlbumHref ? (
        <Link
          to={p.openAlbumHref}
          className={
            (horizontal
              ? "font-display font-bold text-base sm:text-lg md:text-xl "
              : "font-display text-xl md:text-2xl ") +
            "leading-tight text-[var(--ink)] hover:text-[var(--panini-red)] transition-colors block break-words"
          }
          data-testid="album-official-title"
        >
          {p.officialTitle}
        </Link>
      ) : (
        <div
          className={
            (horizontal
              ? "font-display font-bold text-base sm:text-lg md:text-xl "
              : "font-display text-xl md:text-2xl ") +
            "leading-tight text-[var(--ink)] break-words"
          }
          data-testid="album-official-title"
        >
          {p.officialTitle}
        </div>
      )}
      {p.coverLabel && (
        <div
          className="mt-1 text-sm text-[var(--smoke)] truncate min-w-0"
          data-testid="album-cover-label"
        >
          {p.coverLabel}
        </div>
      )}
      {p.collectionMethodLabel && (
        <div
          className="mt-0.5 text-xs text-[var(--smoke)] truncate min-w-0"
          data-testid="album-collection-method"
        >
          <span className="opacity-70">Collection Method:</span>{" "}
          <span className="font-semibold">{p.collectionMethodLabel}</span>
        </div>
      )}
      <div className="mt-2 min-w-0" data-testid="album-nickname">
        {onNicknameSave ? (
          <AlbumNicknameEditor
            value={p.nickname}
            onSave={onNicknameSave}
            testid="album-nickname-editor"
          />
        ) : (
          p.nickname && (
            <span className="text-sm italic text-[var(--smoke)] break-words">
              &ldquo;{p.nickname}&rdquo;
            </span>
          )
        )}
      </div>
    </div>
  );

  const statusStack = (
    <div
      className="flex flex-row sm:flex-col items-start sm:items-end gap-1.5 shrink-0 flex-wrap order-first sm:order-none"
    >
      <StatusPill status={p.status} label={p.statusLabel} />
      {p.isCompleted && (
        <span
          data-testid="album-completed-badge"
          className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-800"
        >
          <Trophy size={11} strokeWidth={2.5} /> 100% Complete
        </span>
      )}
    </div>
  );

  const body = p.isCollectionOnly ? (
    <p
      className="mt-3 text-sm italic text-[var(--smoke)]"
      data-testid="album-collection-only-message"
    >
      {COLLECTION_ONLY_MESSAGE}
    </p>
  ) : (
    <>
      <div className="mt-3">
        <ProgressBar
          percent={p.progressPercent}
          isCompleted={p.isCompleted}
        />
      </div>
      {showCounts &&
        (sCollected || sMissing || sDuplicates) &&
        (p.ownedCount !== null ||
          p.missingCount !== null ||
          p.duplicateCount !== null) && (
          <Counts
            owned={p.ownedCount ?? 0}
            missing={p.missingCount ?? 0}
            duplicate={p.duplicateCount ?? 0}
            showCollected={sCollected}
            showMissing={sMissing}
            showDuplicates={sDuplicates}
          />
        )}
    </>
  );

  const bottomActions =
    (onOpen || manageSlot) && (
      <div className="mt-3 pt-3 border-t border-[var(--stone)] flex items-center justify-between gap-2">
        {onOpen ? (
          <button
            type="button"
            onClick={() => onOpen(album)}
            data-testid="album-open-button"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-black uppercase tracking-widest ${
              variant === "trophy"
                ? "bg-[#c8a24d] text-[#0a0f1c] hover:brightness-110"
                : "bg-[var(--panini-red)] text-white hover:brightness-110"
            } transition`}
          >
            Open Album
          </button>
        ) : (
          <span />
        )}
        {manageSlot}
      </div>
    );

  // ── Horizontal layout ────────────────────────────────────────────
  if (horizontal) {
    return (
      <article
        className={rootClass}
        data-testid="album-snapshot-card"
        data-variant={variant}
        data-layout="horizontal"
        data-status={p.status}
      >
        {trophy && (
          <div className="absolute -top-2 -right-2 rounded-full bg-[#c8a24d] text-[#0a0f1c] p-1.5 shadow-md">
            <Trophy size={14} strokeWidth={2.5} />
          </div>
        )}

        <div className="flex items-start gap-3 sm:gap-4">
          <Thumbnail url={album?.cover_image_url} variant={variant} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-2 sm:gap-3">
              {identity}
              {statusStack}
            </div>
            {body}
          </div>
        </div>

        {footer && (
          <div className="mt-3 pt-3 border-t border-[var(--stone)]">
            {footer}
          </div>
        )}
        {bottomActions}
      </article>
    );
  }

  // ── Default (vertical) layout ────────────────────────────────────
  return (
    <article
      className={rootClass}
      data-testid="album-snapshot-card"
      data-variant={variant}
      data-layout="default"
      data-status={p.status}
    >
      {trophy && (
        <div className="absolute -top-2 -right-2 rounded-full bg-[#c8a24d] text-[#0a0f1c] p-1.5 shadow-md">
          <Trophy size={14} strokeWidth={2.5} />
        </div>
      )}

      {showCover && album?.cover_image_url && (
        <div className="aspect-[3/4] max-h-40 bg-neutral-50 rounded-lg overflow-hidden grid place-items-center border border-[var(--stone)] mb-3">
          <img
            src={album.cover_image_url}
            alt=""
            className="max-w-full max-h-full w-auto h-auto object-contain object-center"
          />
        </div>
      )}
      {showCover && !album?.cover_image_url && trophy && (
        <div className="aspect-[3/4] max-h-40 bg-neutral-50 rounded-lg overflow-hidden grid place-items-center border border-[#c8a24d]/25 mb-3">
          <Package size={36} className="text-[var(--smoke)]" />
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-2 sm:gap-3">
        {identity}
        {statusStack}
      </div>

      {body}

      {footer && (
        <div className="mt-3 pt-3 border-t border-[var(--stone)]">{footer}</div>
      )}
      {bottomActions}
    </article>
  );
}
